import { GlobalColor } from "@builder/components/general/ColorInput";
import OliverText, { TextType } from "@builder/components/general/OliverText";
import DynamicIcon from "@common/components/DynamicIcon";
import { Dispatch, SetStateAction, useCallback } from "react";
import SelectGrid, { SelectGridItem } from "../components/selectGrid/SelectGrid";
import { IconTitle } from "../components/smallComponents/IconTitle";
import { determineBackgroundColor, getContrastingSpectrumColors } from "../helpers/coolColorStuff";
import { colorPalleteTags, Palette, palettes } from "../starterTemplates/colorPalettes";
import { POSOnboardSetup } from "../useApplyOnboardSetup";
import "./step-colors.scss";
import "./style.scss";
import { Upload } from "antd";
import { useUploadAttachmentsMutation } from "@builder/store/slices/attachmentsApiSlice/attachmentsApiSlice";
import { useAppSelector } from "@builder/store/store";
import { useState } from "react";
import Button from "@builder/components/general/Button";

interface StepColorsProps {
    posOnboardSetup: POSOnboardSetup;
    setPOSOnboardSetup: Dispatch<SetStateAction<POSOnboardSetup>>;
}

// helper functions moved to coolColorStuff

const SinglePalette = ({ palette, maxColorsToShow = 4 }: { palette: Palette; maxColorsToShow?: number }) => {
    const isDark = palette.tags.includes("dark");

    const backgroundColor = determineBackgroundColor(palette);

    const featured = palette.featuredColorsPreview?.map(fid => palette.colors.find(c => c.id === fid)).filter((c): c is GlobalColor => !!c) ?? [];

    let colorsToDisplay: GlobalColor[];

    if (featured.length >= maxColorsToShow) {
        colorsToDisplay = featured.slice(0, maxColorsToShow);
    } else {
        const remainingCount = maxColorsToShow - featured.length;
        const featuredIds = new Set(featured.map(c => c.id));
        const availableForFill = palette.colors.filter(c => c.color !== backgroundColor && !featuredIds.has(c.id));
        const fillColors = getContrastingSpectrumColors(availableForFill, remainingCount, backgroundColor);
        colorsToDisplay = [...featured, ...fillColors];
    }

    return (
        <div className="palette-preview" style={{ backgroundColor }}>
            <div className="palette-header">
                <OliverText size="xs" weight="medium" color={isDark ? "white" : "base-blue"}>
                    {palette.name}
                </OliverText>
                <span className="palette-tags">
                    {palette.tags.map(tag => (
                        <DynamicIcon
                            key={tag}
                            name={colorPalleteTags[tag].iconSrc}
                            style={{ width: "16px", height: "16px" }}
                            colors={isDark ? ["#fff", "#fff", "#fff"] : ["var(--base-blue)", "var(--base-blue)", "var(--base-blue)"]}
                        />
                    ))}
                </span>
            </div>
            <div className="palette-colors">
                {colorsToDisplay.slice(0, maxColorsToShow).map((color, idx) => (
                    <span key={idx} className="palette-swatch" style={{ backgroundColor: color.color }} />
                ))}
            </div>
        </div>
    );
};

const StepColors = ({ posOnboardSetup, setPOSOnboardSetup }: StepColorsProps) => {
    const items: SelectGridItem[] = palettes.map(palette => ({
        id: palette.id,
        Node: <SinglePalette key={palette.id} palette={palette} />
    }));

    const handleSelect = useCallback(
        (id: string) => {
            const palette = palettes.find(p => p.id === id);
            if (!palette) return;

            setPOSOnboardSetup(prev => ({
                ...prev,
                colors: {
                    selectedColorPaletteId: id,
                    globalColors: palette.colors
                }
            }));
        },
        [setPOSOnboardSetup]
    );

    const { Dragger } = Upload;
    const [uploadAttachments] = useUploadAttachmentsMutation();
    const [errorMessage, setErrorMessage] = useState<string>("");
    // const company = useAppSelector(state => state.storeData.company);
    const companyId = useAppSelector(state => state.builder.build.companyId);
    const [preview, setPreview] = useState<string | null>(posOnboardSetup.logoUrl ?? null);

    const handleFileFromAntd = async (file: File) => {
        setErrorMessage("");
        if (file.size <= 1_572_864 && file.type.split("/")[0] === "image") {
            if (!companyId) return false;
            try {
                const resp = await uploadAttachments({ companyId: companyId, files: [file] }).unwrap();
                const image = resp[0];
                if (image) {
                    const url = image.originalUrl || image.url;
                    setPreview(url);
                    setPOSOnboardSetup(prev => ({ ...prev, logoUrl: url }));
                }
            } catch (e) {
                console.error("Error uploading logo", e);
            }
        } else {
            setErrorMessage("*Invalid file");
        }
        return false;
    };

    const handleRemoveLogo = () => {
        setPreview(null);
        setPOSOnboardSetup(prev => ({ ...prev, logoUrl: "" }));
    };

    return (
        <div className="single-onboard-step">
            <div className="onboard-upload-logo-container">
                <IconTitle iconUrl="/image-dark.svg" title="Upload your Logo (Optional)" />
                <div className="onboard-upload-logo-dragger">
                    {errorMessage && <OliverText text={errorMessage} size="s" color="red" className="file-input-error" />}
                    <Dragger
                        name="file"
                        multiple={false}
                        showUploadList={false}
                        accept="image/*"
                        beforeUpload={handleFileFromAntd}
                        className="logo-dragger"
                        style={{ background: "white", border: "1px dashed var(--spacer-light)", borderRadius: "4px" }}
                    >
                        <div className="custom-image-input">
                            {!preview && (
                                <>
                                    <img src="./cloud-upload-icon.svg" alt="" />
                                    <OliverText text="Drag & drop here" type={TextType.BODY_2}/>
                                    <OliverText text="or" type={TextType.BODY_2}/>
                                    <div className="fake-button">
                                        <img src="./paperclip-icon.svg" alt="" />
                                        <OliverText text="Choose file" type={TextType.BUTTON_2} color="blue" />
                                    </div>
                                    <OliverText text="Maximum upload size: 512 KB" type={TextType.BODY_4} />
                                </>
                            )}
                            {preview && (
                                <div className="preview-wrapper">
                                    <img src={preview} className="preview-image" />
                                    <Button
                                        buttonStyle="secondary"
                                        size="fit-small"
                                        icon={{ src: "./x-icon-dark.svg" }}
                                        onClick={handleRemoveLogo}
                                        className="remove-logo-btn"
                                    />
                                </div>
                            )}
                        </div>
                    </Dragger>
                </div>
            </div>

            <IconTitle
                iconUrl="/brush-skiny.svg"
                title="Select your colour palette"
                subtitle="You can explore more colour choices and choose your own later."
            />

            <SelectGrid items={items} selectedId={posOnboardSetup.colors?.selectedColorPaletteId} onSelect={handleSelect} />
        </div>
    );
};

export default StepColors;
